<template>
  <div class="flex flex-col h-full overflow-hidden font-[Cubic]">
    <ThreadsNav show-back :show-center="false">
      <template #right>
        <CrystalPowerCount />
      </template>
    </ThreadsNav>
    <div class="flex flex-col flex-1 overflow-auto p-4">
      <!-- Inventory header -->
      <div class="flex justify-between items-center">
        <h2 class="text-lg mb-4">
          {{ $t(`mission.equipment.my`) }}
        </h2>

        <div>
          <button
            class="btn btn-sm btn-outline"
            @click="showDecomposeAllModal = true"
          >
            <i class="iconify mingcute--delete-2-line w-4" />
            一键销毁
          </button>
        </div>
      </div>
      <!-- Inventory grid section -->
      <EquipmentList mode="edit" />

      <!-- Buy button -->
      <div class="flex justify-center mb-4">
        <button
          class="btn btn-primary rounded-full text-white px-4 py-2 btn-sm"
          @click="clickExpandInventory"
        >
          {{ $t(`mission.equipment.buy`) }}
          <CrystalIcon />
        </button>
      </div>
    </div>

    <VanPopup v-model:show="showDecomposeAllModal" class="w-[80%] p-4" round>
      <div class="text-lg font-bold py-4 text-center">一键销毁</div>
      <p class="text-sm text-center text-base-content/50 mb-4">
        被您锁定的物品将不会被销毁
      </p>
      <van-checkbox-group
        v-model="checked"
        shape="square"
        class="flex flex-col gap-2 items-center"
      >
        <van-checkbox
          v-for="rarity in [
            EquipmentRarity.Common,
            EquipmentRarity.Rare,
            EquipmentRarity.Epic,
          ]"
          :key="rarity"
          :name="rarity"
          icon-size="16px"
        >
          <div class="flex items-center gap-1">
            {{ $t(`mission.equipment.rarity.${rarity}`) }}
          </div>
        </van-checkbox>
      </van-checkbox-group>

      <div class="flex justify-center mt-4">
        <div class="flex items-center gap-1">
          总计销毁物品数量: {{ totalCount }}
        </div>
      </div>

      <div class="flex justify-end mt-4 gap-2">
        <button
          class="btn btn-xs btn-outline"
          @click="showDecomposeAllModal = false"
        >
          {{ $t(`Buttons.cancel`) }}
        </button>
        <button class="btn btn-xs btn-primary" @click="clickDecomposeAll">
          {{ $t(`Buttons.confirm`) }}
        </button>
      </div>
    </VanPopup>
  </div>
</template>

<script setup lang="ts">
import { useMission } from "@/stores/mission";
import { useGlobalUser } from "@/stores/globalUser";
import {
  showConfirmDialog,
  showFailToast,
  showLoadingToast,
  showSuccessToast,
} from "vant";
import { useI18n } from "vue-i18n";
import { useGlobalModal } from "@/stores/globalModal";
import { EquipmentRarity, EquipmentType } from "@/enums/mission";
import { computed, ref } from "vue";

const missionStore = useMission();
const userStore = useGlobalUser();
const modalStore = useGlobalModal();
const { t } = useI18n();

const clickExpandInventory = async () => {
  if (modalStore.shouldOpenCrystalNotEnoughModal(5)) return;
  try {
    await showConfirmDialog({
      message: t(`mission.equipment.expandInventoryMessage`),
      confirmButtonText: t(`Buttons.confirm`),
      cancelButtonText: t(`Buttons.cancel`),
    });
  } catch (error) {
    return;
  }
  const loading = showLoadingToast(t(`Message.loading`));
  try {
    await missionStore.expandMateEquipmentInventory();
    await userStore.refresh();
    loading.close();

    showSuccessToast(t(`Message.purchaseSuccess`));
  } catch {
    loading.close();
  }
};

const showDecomposeAllModal = ref(false);
const checked = ref<EquipmentRarity[]>([]);

const totalCount = computed(() => {
  let count = 0;
  for (const rarity of checked.value) {
    count += equipmentRarityCount.value[rarity];
  }
  return count;
});

const equipmentRarityCount = computed<Record<EquipmentRarity, number>>(() => {
  let commonCount = 0;
  let rareCount = 0;
  let epicCount = 0;
  let legendaryCount = 0;
  missionStore.mateEquipmentList.forEach((item) => {
    if (item.type !== EquipmentType.Item || item.isLocked) return;

    if (item.rarity === EquipmentRarity.Common) {
      commonCount++;
    } else if (item.rarity === EquipmentRarity.Rare) {
      rareCount++;
    } else if (item.rarity === EquipmentRarity.Epic) {
      epicCount++;
    } else if (item.rarity === EquipmentRarity.Legendary) {
      legendaryCount++;
    }
  });
  return {
    [EquipmentRarity.Common]: commonCount,
    [EquipmentRarity.Rare]: rareCount,
    [EquipmentRarity.Epic]: epicCount,
    [EquipmentRarity.Legendary]: legendaryCount,
  };
});

const clickDecomposeAll = async () => {
  const loading = showLoadingToast(`努力分解中...`);

  try {
    await missionStore.decomposeManyMateEquipment(checked.value);
    showDecomposeAllModal.value = false;
    loading.close();
    showSuccessToast(t("mission.equipment.recycleSuccess"));
  } catch (e) {
    console.log(e);
    showFailToast(t("mission.equipment.recycleFailed"));
    loading.close();
  }
};
</script>
